import { create } from 'zustand';
import { addEdge, applyNodeChanges, applyEdgeChanges } from 'reactflow';
import * as projectService from '../services/projectService';
import axios from 'axios';
import { clearSourcePickerHandler } from '../utils/sourcePickerBridge';
import { merge3, nodeKey, edgeKey, OUTPUT_KEYS, applyServerOutputs } from '../utils/agentMerge';
import { sameValue, stampExternal, markHydrating, isHydrating } from '../utils/nodeSync';
import { askNodeBusy } from '../services/nodeBusy';
import { recordAd2Metric } from '../utils/ad2Metrics';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Initial nodes for the canvas
const initialNodes = [];
const initialEdges = [];

// SECURITY REVIEW: localStorage stores ONLY the current project UUID.
// No auth tokens, credentials, or PII. Project IDs are non-sensitive opaque UUIDs.
// Auth tokens are managed by Clerk SDK (httpOnly cookies) — never in localStorage.
const CURRENT_PROJECT_KEY = 'intelifotos_current_project_id';

/**
 * Puntero al último proyecto abierto, SIEMPRE por usuario.
 * Sin scope, entrar con otra cuenta (o por la vista previa anónima, que
 * comparte el navegador) restauraba en el canvas los flujos de la sesión
 * anterior. El puntero viejo sin scope se ignora a propósito.
 */
const projectPointerKey = (userKey) => (
  userKey ? `${CURRENT_PROJECT_KEY}::${userKey}` : CURRENT_PROJECT_KEY);

// E1 — Feature flag SAVE_CONFLICT_GUARD. Apagado (false) el cliente no manda
// base_version: el servidor acepta la escritura como siempre y la app se
// comporta exactamente como antes.
export const SAVE_CONFLICT_GUARD =
  process.env.REACT_APP_SAVE_CONFLICT_GUARD !== 'false';

// ============================================
// Project save payload sanitization
// Strips in-memory-only fields and heavy blobs (base64 data: URLs, blob: URLs)
// so projects stored in Supabase stay under ~100 KB regardless of node count.
// ============================================
const MAX_STRING_LEN = 2000;

// blob: URLs are browser-ephemeral and NEVER survive a reload — always drop.
// data: URLs can be tiny (placeholder icons) but real media is huge — drop if > 200.
const isEphemeralUrl = (v) => typeof v === 'string' && v.startsWith('blob:');
const isHeavyDataUrl = (v) =>
  typeof v === 'string' && v.startsWith('data:') && v.length > 200;

const sanitizeNodeData = (data) => {
  if (!data || typeof data !== 'object') return data;
  const out = {};
  for (const [k, v] of Object.entries(data)) {
    if (k === '__extRev') continue;                                 // sólo visual
    if (k === 'file' || k === '_workflowTrigger') continue;        // drop
    if (k === 'uploading') { out[k] = false; continue; }            // reset transient flags
    if (isEphemeralUrl(v) || isHeavyDataUrl(v)) continue;           // drop blob: / large data: urls
    if (typeof v === 'string' && v.length > MAX_STRING_LEN) continue; // drop any oversized string
    out[k] = v;
  }
  // If outputUrl was dropped (blob:/data:) but hasOutput was true, reset hasOutput
  // so the node doesn't render a broken "finished" state on reload.
  if (data.hasOutput && !out.outputUrl) {
    out.hasOutput = false;
  }
  // Preserve preview ONLY if it's a safe http(s) URL from the gallery / R2
  if (typeof data.preview === 'string' && /^https?:\/\//.test(data.preview) && data.preview.length <= MAX_STRING_LEN) {
    out.preview = data.preview;
  } else if (!data.fromAsset) {
    out.preview = null;
  }
  return out;
};

// Envío LMX-6c: en el merge con LMX/MCP los campos de resultado no cuentan
// como edición local (ver OUTPUT_KEYS / applyServerOutputs en agentMerge).
const withoutOutput = (data) => {
  const clean = sanitizeNodeData(data);
  if (!clean || typeof clean !== 'object') return clean;
  const rest = { ...clean };
  OUTPUT_KEYS.forEach((k) => { delete rest[k]; });
  return rest;
};

const sanitizeNodesForSave = (nodes) =>
  (nodes || []).map((n) => ({ ...n, data: sanitizeNodeData(n.data) }));

export const projectSavePayload = (state) => ({
  name: state.projectName,
  description: state.projectDescription,
  nodes: sanitizeNodesForSave(state.nodes),
  edges: state.edges,
  nodeIdCounter: state.nodeIdCounter,
});

export const projectSaveSignature = (state) => JSON.stringify(projectSavePayload(state));

const updateProjectSummary = (projects, project) => {
  const summary = {
    id: project.id, name: project.name, description: project.description,
    node_id_counter: project.node_id_counter, updated_at: project.updated_at,
    version: project.version,
  };
  const exists = (projects || []).some((item) => item.id === project.id);
  return exists
    ? projects.map((item) => (item.id === project.id ? { ...item, ...summary } : item))
    : [summary, ...(projects || [])];
};

let activeProjectSave = null;

// ============================================
// Node Type Definitions for Connection Protocol
// ============================================
const NODE_TYPE_CONFIG = {
  // Input nodes - outputs only, no inputs
  uploadImage: {
    category: 'input',
    outputType: 'image',
    accepts: [],
  },
  uploadVideo: {
    category: 'input',
    outputType: 'video',
    accepts: [],
  },
  uploadAudio: {
    category: 'input',
    outputType: 'audio',
    accepts: [],
  },
  
  // Image processing nodes
  imageEnhancement: {
    category: 'process',
    outputType: 'image',
    accepts: ['image'],
    inputs: { input: 'image' },
  },
  bgRemoval: {
    category: 'process',
    outputType: 'image',
    accepts: ['image'],
    inputs: { input: 'image' },
  },
  upscale: {
    category: 'process',
    outputType: 'image',
    accepts: ['image'],
    inputs: { input: 'image' },
  },
  styleTransfer: {
    category: 'process',
    outputType: 'image',
    accepts: ['image'],
    inputs: { 
      inputStyle: 'image',
      inputStructure: 'image',
    },
  },
  nanoBanana: {
    category: 'process',
    outputType: 'image',
    accepts: ['image'],
    inputs: { input1: 'image', input2: 'image', input3: 'image' },
  },
  frameExtractor: {
    category: 'process',
    outputType: 'image',
    accepts: ['video'],
    inputs: { input: 'video' },
  },
  imageEdit: {
    category: 'process',
    outputType: 'image',
    accepts: ['image'],
    inputs: { input1: 'image', input2: 'image', input3: 'image' },
  },
  banana2pro: {
    category: 'process',
    outputType: 'image',
    accepts: ['image'],
    // Multi-input handle: up to 14 connections on a single port.
    inputs: { multiInput: { type: 'image', multi: true, limit: 14 } },
  },
  imagen4Ultra: {
    category: 'process',
    outputType: 'image',
    // Generación pura desde texto: sin handles de entrada.
    accepts: [],
    inputs: {},
  },
  seedance25: {
    category: 'process',
    outputType: 'video',
    accepts: ['image', 'video', 'audio'],
    inputs: {
      inputImage: 'image',
      inputLastFrame: 'image',
      // Referencias multimodales (imagen/video/audio) en un solo puerto.
      refInput: { type: ['image', 'video', 'audio'], multi: true, limit: 30 },
    },
  },
  pixverseV6: {
    category: 'process',
    outputType: 'video',
    accepts: ['image'],
    inputs: {
      inputImage: 'image',
      inputLastFrame: 'image',
    },
  },
  genQR: {
    category: 'process',
    outputType: 'image',
    accepts: ['image'],
    // Optional single input handle for the center logo.
    inputs: { input: 'image' },
  },
  
  // Video processing nodes
  videoGeneration: {
    category: 'process',
    outputType: 'video',
    accepts: ['image'],
    inputs: { input: 'image' },
  },
  videoWithSound: {
    category: 'process',
    outputType: 'video',
    accepts: ['image', 'video'],
    inputs: { 
      input: 'image',
      videoInput: 'video',
    },
  },
  dreamActor: {
    category: 'process',
    outputType: 'video',
    accepts: ['image', 'video'],
    inputs: { 
      inputImage: 'image',
      inputVideo: 'video',
    },
  },
  fabricAvatar: {
    category: 'process',
    outputType: 'video',
    accepts: ['image', 'audio'],
    inputs: { 
      inputImage: 'image',
      inputAudio: 'audio',
    },
  },
  klingAvatar: {
    category: 'process',
    outputType: 'video',
    accepts: ['image', 'audio'],
    inputs: { 
      inputImage: 'image',
      inputAudio: 'audio',
    },
  },
  
  // Audio processing nodes
  lyria2: {
    category: 'process',
    outputType: 'audio',
    accepts: [],
    inputs: {},
  },
  qwen3tts: {
    category: 'process',
    outputType: 'audio',
    accepts: ['audio'],
    inputs: { inputAudio: 'audio' },
  },
  lyria3pro: {
    category: 'process',
    outputType: 'audio',
    accepts: [],
    inputs: {},
  },
  eleV2: {
    category: 'process',
    outputType: 'audio',
    accepts: [],
    inputs: {},
  },
  elTraductor: {
    category: 'process',
    outputType: 'audio',
    accepts: ['audio', 'video'],
    inputs: {
      inputAudio: 'audio',
      inputVideo: 'video',
    },
  },
  lipSync: {
    category: 'process',
    outputType: 'video',
    accepts: ['video', 'audio'],
    inputs: {
      inputVideo: 'video',
      inputAudio: 'audio',
    },
  },
  musicPro: {
    category: 'process',
    outputType: 'audio',
    accepts: [],
    inputs: {},
  },
  avatarFast: {
    category: 'process',
    outputType: 'video',
    accepts: ['image', 'audio'],
    inputs: {
      inputImage: 'image',
      inputAudio: 'audio',
    },
  },
  
  // Output nodes - accepts any, outputs same type (pass-through)
  outputPreview: {
    category: 'output',
    outputType: 'passthrough', // Outputs whatever it receives
    accepts: ['image', 'video', 'audio'],
    inputs: { input: 'any' },
  },
};

// Envío LMX-4 (I-31): ¿tiene sentido mostrar «Ejecutar flujo completo»?
// Mismas condiciones que exige startWorkflow: al menos un input con archivo
// y al menos un nodo de procesamiento. Sin eso el botón sólo confunde.
export const isRunnableFlow = (nodes) => {
  const list = nodes || [];
  const hasInput = list.some((n) => NODE_TYPE_CONFIG[n.type]?.category === 'input' && n.data?.hasOutput);
  const hasProcess = list.some((n) => NODE_TYPE_CONFIG[n.type]?.category === 'process');
  return hasInput && hasProcess;
};

// Get compatible source types for a target handle
const getCompatibleSourceTypes = (targetNodeType, targetHandle) => {
  const config = NODE_TYPE_CONFIG[targetNodeType];
  if (!config) return [];
  
  // If no specific input mapping, accept based on general accepts
  if (!config.inputs || !config.inputs[targetHandle]) {
    return config.accepts || [];
  }
  
  const expectedType = config.inputs[targetHandle];
  // Multi-input handle stored as an object: { type, multi, limit }
  if (expectedType && typeof expectedType === 'object') {
    const t = expectedType.type;
    if (!t || t === 'any') return ['image', 'video', 'audio'];
    return Array.isArray(t) ? t : [t];
  }
  if (expectedType === 'any') {
    return ['image', 'video', 'audio'];
  }
  
  return [expectedType];
};

// Get output type of a node
const getNodeOutputType = (nodeType) => {
  const config = NODE_TYPE_CONFIG[nodeType];
  return config?.outputType || null;
};

export const useWorkflowStore = create((set, get) => ({
  // React Flow state
  nodes: initialNodes,
  edges: initialEdges,
  
  // Selected node for properties panel
  selectedNode: null,
  
  // Assets (results from AI operations)
  assets: [],
  
  // Processing state
  processingNodes: new Set(),
  
  // Workflow execution state
  isRunningWorkflow: false,
  workflowQueue: [],
  workflowCurrentIndex: -1,
  workflowError: null,
  
  // Node ID counter
  nodeIdCounter: 1,
  
  // Footer/Badge visibility
  showFooter: true,
  showBadge: false,
  
  // Project management
  currentProjectId: null,
  currentProjectData: null,
  currentProjectVersion: null,
  savedProjects: [],
  projectName: 'Proyecto sin título',
  projectDescription: '',
  isLoadingProjects: false,
  isSavingProject: false,
  projectError: null,
  hasUnsavedChanges: false,
  
  // Source picker state (for node file loading modal)
  sourcePicker: null,

  // ── E6c.5 · envíos múltiples de un mismo nodo ──────────────────────
  // inFlight: generaciones en curso POR NODO (del backend + locales).
  //   { node_7: [{ execution_id, status, started_at, result_type }] }
  inFlight: {},
  // La última generación MANDADA de cada nodo: es la única que puede escribir
  // en el nodo. Las anteriores terminan y quedan sólo en la galería.
  nodeActiveExecution: {},
  // Cuántas generaciones viejas terminaron sin poder mostrarse en el nodo.
  galleryNotice: {},

  setNodeInFlight: (nodeId, list) => set((state) => {
    const next = { ...state.inFlight };
    if (!list || list.length === 0) delete next[nodeId];
    else next[nodeId] = list;
    return { inFlight: next };
  }),

  /** Reemplaza el mapa completo (viene de /executions/active/list). */
  replaceInFlight: (map) => set({ inFlight: map || {} }),

  addInFlight: (nodeId, execution) => set((state) => {
    if (!nodeId || !execution?.execution_id) return {};
    const list = state.inFlight[nodeId] || [];
    if (list.some((e) => e.execution_id === execution.execution_id)) return {};
    return { inFlight: { ...state.inFlight, [nodeId]: [...list, execution] } };
  }),

  removeInFlight: (nodeId, executionId) => set((state) => {
    const list = state.inFlight[nodeId];
    if (!list) return {};
    const next = list.filter((e) => e.execution_id !== executionId);
    const map = { ...state.inFlight };
    if (next.length) map[nodeId] = next; else delete map[nodeId];
    return { inFlight: map };
  }),

  setNodeActiveExecution: (nodeId, executionId) => set((state) => ({
    nodeActiveExecution: { ...state.nodeActiveExecution, [nodeId]: executionId },
  })),

  /** true si esa ejecución sigue siendo la que el nodo tiene que mostrar. */
  isActiveExecution: (nodeId, executionId) =>
    get().nodeActiveExecution[nodeId] === executionId,

  bumpGalleryNotice: (nodeId) => set((state) => ({
    galleryNotice: {
      ...state.galleryNotice,
      [nodeId]: (state.galleryNotice[nodeId] || 0) + 1,
    },
  })),

  clearGalleryNotice: (nodeId) => set((state) => {
    const next = { ...state.galleryNotice };
    delete next[nodeId];
    return { galleryNotice: next };
  }),

  /** Cantidad total de generaciones en curso en el canvas (para el botón). */
  countInFlight: () => Object.values(get().inFlight)
    .reduce((acc, list) => acc + list.length, 0),

  /**
   * Avisa si alguno de estos nodos ya tiene generaciones en curso y espera la
   * decisión del usuario. Devuelve 'send' | 'stop_and_send' | 'cancel'.
   * Con 'stop_and_send' detiene las anteriores antes de devolver.
   */
  confirmBusyNodes: async (nodeIds, { cost = null, source = 'node' } = {}) => {
    const state = get();
    const busy = nodeIds
      .map((nodeId) => ({
        nodeId,
        running: (state.inFlight[nodeId] || []).length,
        label: state.nodes.find((n) => n.id === nodeId)?.data?.name
          || state.nodes.find((n) => n.id === nodeId)?.type
          || nodeId,
      }))
      .filter((n) => n.running > 0);
    if (busy.length === 0) return 'send';

    const answer = await askNodeBusy({ nodes: busy, cost, source });
    if (answer === 'stop_and_send') {
      const { stopExecution } = await import('../services/stopExecution');
      for (const n of busy) {
        for (const exec of get().inFlight[n.nodeId] || []) {
          // eslint-disable-next-line no-await-in-loop
          await stopExecution(exec.execution_id, { confirm: false });
        }
        get().setNodeInFlight(n.nodeId, []);
      }
      window.dispatchEvent(new Event('executions:changed'));
    }
    return answer;
  },
  
  // Actions
  setNodes: (nodes) => set({ nodes, hasUnsavedChanges: true }),
  setEdges: (edges) => set({ edges, hasUnsavedChanges: true }),

  // Source picker actions
  openSourcePicker: (nodeId, acceptTypes, acceptInput) => set({
    sourcePicker: { nodeId, acceptTypes, acceptInput, step: 'choose' },
  }),
  setSourcePickerStep: (step) => set((state) => ({
    sourcePicker: state.sourcePicker ? { ...state.sourcePicker, step } : null,
  })),
  closeSourcePicker: () => {
    clearSourcePickerHandler();
    set({ sourcePicker: null });
  },
  
  onNodesChange: (changes) => {
    // 'dimensions' lo emite React Flow al MEDIR los nodos (al cargar, zoom,
    // re-render): no es un cambio del usuario. Sí cuenta si lo está
    // redimensionando a mano (resizing).
    // 'position' también se emite con un simple click (React Flow reafirma la
    // posición al seleccionar): sólo cuenta si el nodo se movió de verdad.
    const prev = get().nodes;
    const reallyMoved = (c) => {
      if (!c.position) return false;
      const old = prev.find((n) => n.id === c.id);
      if (!old?.position) return true;
      return Math.abs(old.position.x - c.position.x) > 0.5
        || Math.abs(old.position.y - c.position.y) > 0.5;
    };
    const hasRealChanges = changes.some((c) => {
      if (c.type === 'select') return false;
      if (c.type === 'dimensions') return !!c.resizing;
      if (c.type === 'position') return reallyMoved(c);
      return true;
    });
    set({
      nodes: applyNodeChanges(changes, get().nodes),
      ...(hasRealChanges ? { hasUnsavedChanges: true } : {}),
    });
  },
  
  onEdgesChange: (changes) => {
    // Mismo criterio que onNodesChange: seleccionar un nodo también emite
    // cambios 'select' en sus edges, y eso no es un cambio del usuario.
    const hasRealChanges = changes.some(c => c.type !== 'select');
    set({
      edges: applyEdgeChanges(changes, get().edges),
      ...(hasRealChanges ? { hasUnsavedChanges: true } : {}),
    });
  },
  
  onConnect: (connection) => {
    // Validate connection before adding
    const nodes = get().nodes;
    const sourceNode = nodes.find(n => n.id === connection.source);
    const targetNode = nodes.find(n => n.id === connection.target);
    
    if (!sourceNode || !targetNode) return;
    
    const sourceType = getNodeOutputType(sourceNode.type);
    const targetNodeType = targetNode.type;
    const targetHandle = connection.targetHandle || 'input';
    
    // Handle passthrough (outputPreview stores the type it received)
    let effectiveSourceType = sourceType;
    if (sourceType === 'passthrough' && sourceNode.data?.outputType) {
      effectiveSourceType = sourceNode.data.outputType;
    }
    
    const compatibleTypes = getCompatibleSourceTypes(targetNodeType, targetHandle);
    
    // If target accepts any or the source type is compatible
    if (compatibleTypes.length === 0 || 
        compatibleTypes.includes(effectiveSourceType) || 
        compatibleTypes.includes('any')) {
      set({
        edges: addEdge(
          { 
            ...connection, 
            animated: true, 
            style: { stroke: '#10b981', strokeWidth: 2 },
            data: { sourceType: effectiveSourceType },
          },
          get().edges
        ),
        hasUnsavedChanges: true,
      });
    } else {
      console.warn(`Invalid connection: ${sourceNode.type} (${effectiveSourceType}) cannot connect to ${targetNodeType}.${targetHandle} (accepts: ${compatibleTypes.join(', ')})`);
    }
  },
  
  // Validate if a connection would be valid (for isValidConnection callback)
  validateConnection: (connection) => {
    const nodes = get().nodes;
    const edges = get().edges;
    
    // Don't allow self connections
    if (connection.source === connection.target) return false;
    
    // Source must be an output handle
    if (connection.sourceHandle !== 'output') return false;
    
    // Target must be a known input handle (supports both "input*" naming
    // and the explicit multi-input handle id "multiInput")
    const targetHandleId = connection.targetHandle || 'input';
    const targetNode = nodes.find(n => n.id === connection.target);
    if (!targetNode) return false;
    const targetCfg = NODE_TYPE_CONFIG[targetNode.type];
    const inputCfg = targetCfg?.inputs?.[targetHandleId];
    const isKnownInput =
      targetHandleId.startsWith('input') ||
      targetHandleId === 'multiInput' ||
      inputCfg !== undefined;
    if (!isKnownInput) return false;

    // Determine if this target handle allows multiple inbound edges.
    const isMulti =
      (inputCfg && typeof inputCfg === 'object' && inputCfg.multi === true) ||
      targetHandleId === 'multiInput';

    // Existing edges already on this exact target handle
    const existingEdgesOnHandle = edges.filter(
      (edge) => edge.target === connection.target && edge.targetHandle === targetHandleId
    );

    if (!isMulti) {
      // Single-input handle: reject if already connected
      if (existingEdgesOnHandle.length > 0) return false;
    } else {
      // Multi-input handle: enforce per-handle limit if configured
      const limit = (inputCfg && typeof inputCfg === 'object' && inputCfg.limit) || Infinity;
      if (existingEdgesOnHandle.length >= limit) return false;
      // Prevent duplicate edges from the same source to the same multi handle
      const duplicate = existingEdgesOnHandle.some((e) => e.source === connection.source);
      if (duplicate) return false;
    }
    
    // Validate type compatibility
    const sourceNode = nodes.find(n => n.id === connection.source);
    
    if (!sourceNode) return false;
    
    const sourceType = getNodeOutputType(sourceNode.type);
    let effectiveSourceType = sourceType;
    
    // Handle passthrough nodes
    if (sourceType === 'passthrough' && sourceNode.data?.outputType) {
      effectiveSourceType = sourceNode.data.outputType;
    }
    
    const compatibleTypes = getCompatibleSourceTypes(targetNode.type, targetHandleId);
    
    // If no restrictions or type is compatible
    if (compatibleTypes.length === 0 || 
        compatibleTypes.includes(effectiveSourceType) || 
        compatibleTypes.includes('any')) {
      return true;
    }
    
    return false;
  },
  
  // Get connection protocol info for UI hints
  getConnectionInfo: (nodeType, handleId) => {
    const config = NODE_TYPE_CONFIG[nodeType];
    if (!config) return null;
    
    return {
      category: config.category,
      outputType: config.outputType,
      accepts: config.accepts,
      inputType: config.inputs?.[handleId] || null,
    };
  },
  
  setSelectedNode: (node) => set({ selectedNode: node }),
  
  // Add a new node to the canvas
  addNode: (type, position, data = {}) => {
    // Safety: ensure nodeIdCounter is higher than any existing node ID
    const existingNodes = get().nodes;
    const maxExistingId = existingNodes.reduce((max, node) => {
      const match = node.id?.match(/^node_(\d+)$/);
      return match ? Math.max(max, parseInt(match[1], 10)) : max;
    }, 0);
    const safeCounter = Math.max(get().nodeIdCounter, maxExistingId + 1);
    
    const id = `node_${safeCounter}`;
    const nodeCount = existingNodes.length;
    
    // Default positions that spread nodes horizontally
    const defaultPosition = position || { 
      x: 100 + nodeCount * 320,
      y: 150 + (nodeCount % 2) * 50
    };
    
    const newNode = {
      id,
      type,
      position: defaultPosition,
      data: {
        ...data,
        nodeId: id,
      },
    };
    
    set({
      nodes: [...existingNodes, newNode],
      nodeIdCounter: safeCounter + 1,
      hasUnsavedChanges: true,
    });
    
    return id;
  },
  
  // Update node data
  updateNodeData: (nodeId, newData) => {
    const s = get();
    const node = s.nodes.find((n) => n.id === nodeId);
    if (!node) return;
    const keys = Object.keys(newData || {});
    if (!keys.some((k) => !sameValue(node.data?.[k], newData[k]))) return; // no cambió nada
    const patch = (n) => (n.id === nodeId ? { ...n, data: { ...n.data, ...newData } } : n);
    const nodes = s.nodes.map(patch);
    if (isHydrating(nodeId)) {
      // Escritura de montaje (el nodo copia su data al estado local y la
      // devuelve): no es un cambio del usuario → sin punto amarillo, y se
      // suma a la base del merge para no confundirla con un cambio local.
      const base = s.currentProjectData;
      set({ nodes, ...(base ? { currentProjectData: { ...base, nodes: (base.nodes || []).map(patch) } } : {}) });
      return;
    }
    set({ nodes, hasUnsavedChanges: true });
  },
  
  // Delete a node
  deleteNode: (nodeId) => {
    set({
      nodes: get().nodes.filter((node) => node.id !== nodeId),
      edges: get().edges.filter(
        (edge) => edge.source !== nodeId && edge.target !== nodeId
      ),
      selectedNode: get().selectedNode?.id === nodeId ? null : get().selectedNode,
      hasUnsavedChanges: true,
    });
  },
  
  // Add processing node
  setNodeProcessing: (nodeId, isProcessing) => {
    const newProcessingNodes = new Set(get().processingNodes);
    if (isProcessing) {
      newProcessingNodes.add(nodeId);
    } else {
      newProcessingNodes.delete(nodeId);
    }
    set({ processingNodes: newProcessingNodes });
  },
  
  // Asset management
  addAsset: (asset) => {
    set({ assets: [asset, ...get().assets] });
  },
  
  loadAssets: (assets) => {
    set({ assets });
  },
  
  // Get connected input for a node
  getInputForNode: (nodeId) => {
    const edges = get().edges;
    const nodes = get().nodes;
    
    const inputEdge = edges.find((edge) => edge.target === nodeId);
    if (!inputEdge) return null;
    
    const sourceNode = nodes.find((node) => node.id === inputEdge.source);
    return sourceNode?.data;
  },
  
  // Get connected input for a specific handle
  getInputForHandle: (nodeId, handleId) => {
    const edges = get().edges;
    const nodes = get().nodes;
    
    const inputEdge = edges.find(
      (edge) => edge.target === nodeId && edge.targetHandle === handleId
    );
    if (!inputEdge) return null;
    
    const sourceNode = nodes.find((node) => node.id === inputEdge.source);
    return sourceNode?.data;
  },
  
  // ============================================
  // Workflow Execution Engine
  // ============================================

  // Get all downstream nodes from a starting node (BFS)
  getDownstreamNodes: (startNodeId) => {
    const edges = get().edges;
    const visited = new Set();
    const queue = [startNodeId];
    const result = [];

    while (queue.length > 0) {
      const current = queue.shift();
      const outgoing = edges.filter(e => e.source === current);
      for (const edge of outgoing) {
        if (!visited.has(edge.target)) {
          visited.add(edge.target);
          result.push(edge.target);
          queue.push(edge.target);
        }
      }
    }
    return result;
  },

  // Topological sort — returns ordered array of all node IDs
  getExecutionOrder: () => {
    const nodes = get().nodes;
    const edges = get().edges;

    const inDegree = {};
    const adjList = {};
    nodes.forEach(n => { inDegree[n.id] = 0; adjList[n.id] = []; });
    edges.forEach(e => {
      if (inDegree[e.target] !== undefined) inDegree[e.target]++;
      if (adjList[e.source]) adjList[e.source].push(e.target);
    });

    // Kahn's algorithm
    const queue = nodes.filter(n => inDegree[n.id] === 0).map(n => n.id);
    const order = [];

    while (queue.length > 0) {
      const nodeId = queue.shift();
      order.push(nodeId);
      for (const downstream of (adjList[nodeId] || [])) {
        inDegree[downstream]--;
        if (inDegree[downstream] === 0) queue.push(downstream);
      }
    }
    return order;
  },

  // Reset a single node's output data
  resetNodeOutput: (nodeId) => {
    set({
      nodes: get().nodes.map(node =>
        node.id === nodeId
          ? { ...node, data: { ...node.data, hasOutput: false, outputUrl: null, file: null, processedAt: null, _workflowTrigger: 0 } }
          : node
      ),
      hasUnsavedChanges: true,
    });
  },

  // Reset all downstream processing nodes from a given node
  resetDownstream: (startNodeId) => {
    const downstream = get().getDownstreamNodes(startNodeId);
    if (downstream.length === 0) return;

    set({
      nodes: get().nodes.map(node => {
        if (downstream.includes(node.id)) {
          const config = NODE_TYPE_CONFIG[node.type];
          if (config?.category === 'process') {
            return { ...node, data: { ...node.data, hasOutput: false, outputUrl: null, file: null, processedAt: null, _workflowTrigger: 0 } };
          }
        }
        return node;
      }),
      hasUnsavedChanges: true,
    });
  },

  // Start full workflow execution
  startWorkflow: async () => {
    if (get().currentProjectId && get().hasUnsavedChanges) {
      await get().flushProjectSave();
      if (get().hasUnsavedChanges) throw new Error('No se pudo guardar el proyecto antes de ejecutar');
    }
    const state = get();

    // Check for source nodes with outputs (uploaded files)
    const sourceNodes = state.nodes.filter(n => {
      const config = NODE_TYPE_CONFIG[n.type];
      return config?.category === 'input' && n.data?.hasOutput;
    });
    if (sourceNodes.length === 0) {
      return { error: 'No hay inputs listos. Sube archivos primero.' };
    }

    const order = state.getExecutionOrder();

    // Filter to only processing nodes
    const processableQueue = order.filter(nodeId => {
      const node = state.nodes.find(n => n.id === nodeId);
      const config = NODE_TYPE_CONFIG[node?.type];
      return config?.category === 'process';
    });
    if (processableQueue.length === 0) {
      return { error: 'No hay nodos de procesamiento en el flujo.' };
    }

    // E6c.5 · un solo aviso que lista TODOS los nodos ocupados del flujo.
    // Antes de preguntar no se resetea nada: cancelar deja todo como estaba.
    const answer = await get().confirmBusyNodes(processableQueue, { source: 'workflow' });
    if (answer === 'cancel') return { cancelled: true, error: null };

    // Reset all processing nodes in the queue
    const resetNodes = get().nodes.map(node => {
      if (processableQueue.includes(node.id)) {
        return { ...node, data: { ...node.data, hasOutput: false, outputUrl: null, file: null, processedAt: null, _workflowTrigger: 0 } };
      }
      return node;
    });

    set({
      nodes: resetNodes,
      isRunningWorkflow: true,
      workflowQueue: processableQueue,
      workflowCurrentIndex: 0,
      workflowError: null,
      busyConfirmed: answer === 'send'
        ? [...processableQueue]   // ya preguntamos: los nodos no vuelven a preguntar
        : [],
    });

    // Trigger first node after a brief delay for state to settle
    setTimeout(() => {
      get().triggerWorkflowNode(processableQueue[0]);
    }, 150);

    return { error: null, total: processableQueue.length };
  },

  // Nodos cuyo envío paralelo ya aprobó el usuario en este arranque: el hook
  // del nodo lo consume una vez y no vuelve a preguntar.
  busyConfirmed: [],
  consumeBusyConfirmed: (nodeId) => {
    const list = get().busyConfirmed;
    if (!list.includes(nodeId)) return false;
    set({ busyConfirmed: list.filter((id) => id !== nodeId) });
    return true;
  },

  // Trigger a specific node for workflow execution
  triggerWorkflowNode: (nodeId) => {
    set({
      nodes: get().nodes.map(node =>
        node.id === nodeId
          ? { ...node, data: { ...node.data, _workflowTrigger: Date.now() } }
          : node
      ),
    });
  },

  // Called by a node after it finishes processing during a workflow run
  workflowNodeCompleted: (nodeId, success) => {
    const state = get();
    if (!state.isRunningWorkflow) return;

    if (!success) {
      set({
        isRunningWorkflow: false,
        workflowCurrentIndex: -1,
        workflowError: `Error en nodo: ${nodeId}`,
      });
      return;
    }

    const nextIndex = state.workflowCurrentIndex + 1;
    if (nextIndex >= state.workflowQueue.length) {
      // Workflow complete
      set({
        isRunningWorkflow: false,
        workflowCurrentIndex: -1,
        workflowError: null,
      });
      return;
    }

    set({ workflowCurrentIndex: nextIndex });

    // Trigger next node with delay so React can propagate data from completed node
    setTimeout(() => {
      get().triggerWorkflowNode(state.workflowQueue[nextIndex]);
    }, 300);
  },

  // Cancel a running workflow
  cancelWorkflow: () => {
    set({
      isRunningWorkflow: false,
      workflowQueue: [],
      workflowCurrentIndex: -1,
      workflowError: null,
    });
  },

  /**
   * Batch helper — run `startWorkflow` and resolve when the workflow
   * transitions back to `isRunningWorkflow=false`. Rejects on workflowError.
   * Never mutates workflow state itself; only observes it.
   *
   * Returned promise:
   *   - resolves { total } on clean finish
   *   - rejects Error(workflowError) on node failure or start error
   */
  runWorkflowAndWait: () => new Promise((resolve, reject) => {
    // Guard against concurrent starts — refuse if already running.
    if (get().isRunningWorkflow) {
      reject(new Error('Workflow ya está en ejecución'));
      return;
    }
    let unsub = null;
    get().startWorkflow().then((start) => {
      if (start.error) { reject(new Error(start.error)); return; }
      if (start.cancelled) { resolve({ total: 0, cancelled: true }); return; }
      unsub = useWorkflowStore.subscribe((state, prevState) => {
        // Fire once when the workflow transitions from running -> not-running.
        if (prevState.isRunningWorkflow && !state.isRunningWorkflow) {
          unsub();
          if (state.workflowError) reject(new Error(state.workflowError));
          else resolve({ total: start.total });
        }
      });
    }).catch(reject);
  }),

  /**
   * Batch helper — run only a subset of processing nodes (used by the
   * batch runner to execute exactly the nodes selected between the
   * user's INICIO and FIN markers). `subsetProcessIds` must already be
   * topologically ordered and consist of nodes with category='process'.
   *
   * Same completion semantics as runWorkflowAndWait.
   */
  startPartialWorkflow: async (subsetProcessIds) => {
    const state = get();
    if (!Array.isArray(subsetProcessIds) || subsetProcessIds.length === 0) {
      return { error: 'La cola parcial está vacía.' };
    }
    const answer = await get().confirmBusyNodes(subsetProcessIds, { source: 'partial' });
    if (answer === 'cancel') return { cancelled: true, error: null };
    // Reset outputs of every node in the subset so old results don't leak.
    const resetNodes = get().nodes.map((node) => {
      if (subsetProcessIds.includes(node.id)) {
        return { ...node, data: { ...node.data, hasOutput: false, outputUrl: null, file: null, processedAt: null, _workflowTrigger: 0 } };
      }
      return node;
    });
    set({
      nodes: resetNodes,
      isRunningWorkflow: true,
      workflowQueue: [...subsetProcessIds],
      workflowCurrentIndex: 0,
      workflowError: null,
      busyConfirmed: answer === 'send' ? [...subsetProcessIds] : [],
    });
    setTimeout(() => {
      get().triggerWorkflowNode(subsetProcessIds[0]);
    }, 150);
    return { error: null, total: subsetProcessIds.length };
  },

  runPartialWorkflowAndWait: (subsetProcessIds) => new Promise((resolve, reject) => {
    if (get().isRunningWorkflow) {
      reject(new Error('Workflow ya está en ejecución'));
      return;
    }
    let unsub = null;
    get().startPartialWorkflow(subsetProcessIds).then((start) => {
      if (start.error) { reject(new Error(start.error)); return; }
      if (start.cancelled) { resolve({ total: 0, cancelled: true }); return; }
      unsub = useWorkflowStore.subscribe((s, prevState) => {
        if (prevState.isRunningWorkflow && !s.isRunningWorkflow) {
          unsub();
          if (s.workflowError) reject(new Error(s.workflowError));
          else resolve({ total: start.total });
        }
      });
    }).catch(reject);
  }),

  // Rerun a single node and all its downstream dependents
  rerunFromNode: async (nodeId) => {
    const state = get();
    if (state.isRunningWorkflow) return { error: 'Ya hay un flujo en ejecución.' };

    const node = state.nodes.find(n => n.id === nodeId);
    if (!node) return { error: 'Nodo no encontrado.' };

    // Build the execution queue: this node + downstream processing nodes in topological order
    const downstreamIds = state.getDownstreamNodes(nodeId);
    const allIds = [nodeId, ...downstreamIds];

    // Get global topological order, filter to only our target set and processing nodes
    const globalOrder = state.getExecutionOrder();
    const queue = globalOrder.filter(nId => {
      if (!allIds.includes(nId)) return false;
      const n = state.nodes.find(nd => nd.id === nId);
      const config = NODE_TYPE_CONFIG[n?.type];
      return config?.category === 'process';
    });

    if (queue.length === 0) return { error: 'No hay nodos procesables.' };

    const answer = await get().confirmBusyNodes(queue, { source: 'rerun' });
    if (answer === 'cancel') return { cancelled: true, error: null };

    // Reset all nodes in the queue
    const resetNodes = get().nodes.map(n => {
      if (queue.includes(n.id)) {
        return { ...n, data: { ...n.data, hasOutput: false, outputUrl: null, file: null, processedAt: null, _workflowTrigger: 0 } };
      }
      return n;
    });

    set({
      nodes: resetNodes,
      isRunningWorkflow: true,
      workflowQueue: queue,
      workflowCurrentIndex: 0,
      workflowError: null,
      busyConfirmed: answer === 'send' ? [...queue] : [],
    });

    setTimeout(() => {
      get().triggerWorkflowNode(queue[0]);
    }, 150);

    return { error: null, total: queue.length };
  },

  // Toggle footer/badge
  toggleFooter: () => set({ showFooter: !get().showFooter }),
  toggleBadge: () => set({ showBadge: !get().showBadge }),
  
  // Clear canvas — resets the scenario completely, dissociates from current project
  clearCanvas: () => {
    set({ 
      nodes: [], 
      edges: [], 
      selectedNode: null,
      nodeIdCounter: 1,
      currentProjectId: null,
      currentProjectData: null,
      projectName: 'Proyecto sin título',
      projectDescription: '',
      hasUnsavedChanges: false,
    });
    localStorage.removeItem(projectPointerKey(get().userKey));
  },
  
  // ============================================
  // Quick Start Template
  // ============================================
  createQuickStartWorkflow: () => {
    // Clear existing nodes
    set({ nodes: [], edges: [], nodeIdCounter: 1 });
    
    // Create Upload node
    const uploadId = `node_1`;
    const uploadNode = {
      id: uploadId,
      type: 'uploadImage',
      position: { x: 100, y: 200 },
      data: { nodeId: uploadId },
    };
    
    // Create Enhancement node
    const enhanceId = `node_2`;
    const enhanceNode = {
      id: enhanceId,
      type: 'imageEnhancement',
      position: { x: 420, y: 200 },
      data: { nodeId: enhanceId },
    };
    
    // Create Output node
    const outputId = `node_3`;
    const outputNode = {
      id: outputId,
      type: 'outputPreview',
      position: { x: 740, y: 200 },
      data: { nodeId: outputId },
    };
    
    // Create edges
    const edge1 = {
      id: 'edge_1_2',
      source: uploadId,
      target: enhanceId,
      sourceHandle: 'output',
      targetHandle: 'input',
      animated: true,
      style: { stroke: '#10b981', strokeWidth: 2 },
    };
    
    const edge2 = {
      id: 'edge_2_3',
      source: enhanceId,
      target: outputId,
      sourceHandle: 'output',
      targetHandle: 'input',
      animated: true,
      style: { stroke: '#10b981', strokeWidth: 2 },
    };
    
    set({
      nodes: [uploadNode, enhanceNode, outputNode],
      edges: [edge1, edge2],
      nodeIdCounter: 4,
      selectedNode: null,
    });
  },
  
  // Create Video workflow template
  createVideoWorkflow: () => {
    set({ nodes: [], edges: [], nodeIdCounter: 1 });
    
    const uploadId = `node_1`;
    const uploadNode = {
      id: uploadId,
      type: 'uploadImage',
      position: { x: 100, y: 200 },
      data: { nodeId: uploadId },
    };
    
    const videoId = `node_2`;
    const videoNode = {
      id: videoId,
      type: 'videoGeneration',
      position: { x: 420, y: 200 },
      data: { nodeId: videoId },
    };
    
    const outputId = `node_3`;
    const outputNode = {
      id: outputId,
      type: 'outputPreview',
      position: { x: 740, y: 200 },
      data: { nodeId: outputId },
    };
    
    const edge1 = {
      id: 'edge_1_2',
      source: uploadId,
      target: videoId,
      sourceHandle: 'output',
      targetHandle: 'input',
      animated: true,
      style: { stroke: '#10b981', strokeWidth: 2 },
    };
    
    const edge2 = {
      id: 'edge_2_3',
      source: videoId,
      target: outputId,
      sourceHandle: 'output',
      targetHandle: 'input',
      animated: true,
      style: { stroke: '#10b981', strokeWidth: 2 },
    };
    
    set({
      nodes: [uploadNode, videoNode, outputNode],
      edges: [edge1, edge2],
      nodeIdCounter: 4,
      selectedNode: null,
    });
  },

  // Video Flow template: Video → Frame Extractor → Crear Video → Frame Extractor → Preview
  createVideoFlowWorkflow: () => {
    set({ nodes: [], edges: [], nodeIdCounter: 1 });

    const uploadVideoId = 'node_1';
    const uploadVideoNode = {
      id: uploadVideoId,
      type: 'uploadVideo',
      position: { x: 50, y: 200 },
      data: { nodeId: uploadVideoId },
    };

    const frame1Id = 'node_2';
    const frame1Node = {
      id: frame1Id,
      type: 'frameExtractor',
      position: { x: 340, y: 200 },
      data: { nodeId: frame1Id, returnFirstFrame: false },
    };

    const videoGenId = 'node_3';
    const videoGenNode = {
      id: videoGenId,
      type: 'videoGeneration',
      position: { x: 630, y: 200 },
      data: { nodeId: videoGenId },
    };

    const frame2Id = 'node_4';
    const frame2Node = {
      id: frame2Id,
      type: 'frameExtractor',
      position: { x: 920, y: 200 },
      data: { nodeId: frame2Id, returnFirstFrame: false },
    };

    const outputId = 'node_5';
    const outputNode = {
      id: outputId,
      type: 'outputPreview',
      position: { x: 1210, y: 200 },
      data: { nodeId: outputId },
    };

    const edgeStyle = { stroke: '#0ea5e9', strokeWidth: 2 };

    set({
      nodes: [uploadVideoNode, frame1Node, videoGenNode, frame2Node, outputNode],
      edges: [
        { id: 'edge_1_2', source: uploadVideoId, target: frame1Id, sourceHandle: 'output', targetHandle: 'input', animated: true, style: edgeStyle },
        { id: 'edge_2_3', source: frame1Id, target: videoGenId, sourceHandle: 'output', targetHandle: 'input', animated: true, style: edgeStyle },
        { id: 'edge_3_4', source: videoGenId, target: frame2Id, sourceHandle: 'output', targetHandle: 'input', animated: true, style: edgeStyle },
        { id: 'edge_4_5', source: frame2Id, target: outputId, sourceHandle: 'output', targetHandle: 'input', animated: true, style: edgeStyle },
      ],
      nodeIdCounter: 6,
      selectedNode: null,
    });
  },
  
  // ============================================
  // Project Management - Save/Load (MongoDB API)
  // ============================================
  setProjectName: (name) => set({ projectName: name, hasUnsavedChanges: true }),
  setProjectDescription: (desc) => set({ projectDescription: desc, hasUnsavedChanges: true }),
  setHasUnsavedChanges: (value) => set({ hasUnsavedChanges: value }),
  
  // Save current workflow as a new project
  saveProjectAsNew: async (name, description = '') => {
    const state = get();
    set({ isSavingProject: true, projectError: null });
    
    try {
      const projectData = {
        name: name || state.projectName,
        description: description || state.projectDescription,
        nodes: sanitizeNodesForSave(state.nodes),
        edges: state.edges,
        nodeIdCounter: state.nodeIdCounter,
      };
      
      const result = await projectService.createProject(projectData);
      
      if (result.success) {
        set({
          currentProjectId: result.project.id,
          currentProjectData: result.project,
          currentProjectVersion: result.project?.version ?? null,
          projectName: result.project.name,
          projectDescription: result.project.description || '',
          savedProjects: updateProjectSummary(get().savedProjects, result.project),
          isSavingProject: false,
          hasUnsavedChanges: false,
        });
        
        localStorage.setItem(projectPointerKey(get().userKey), result.project.id);
        return result.project;
      }
      
      throw new Error('Failed to save project');
    } catch (error) {
      set({ isSavingProject: false, projectError: error.message });
      console.error('Error saving project:', error);
      throw error;
    }
  },
  
  // Serializa escrituras: dos disparadores simultáneos comparten la misma promesa.
  saveProject: async (options = {}) => {
    if (activeProjectSave) {
      recordAd2Metric('save.deduplicated');
      return activeProjectSave;
    }
    const state = get();
    if (!options.force && state.currentProjectId && !state.hasUnsavedChanges) {
      recordAd2Metric('save.skipped_unchanged');
      return state.currentProjectData;
    }
    activeProjectSave = get()._saveProjectOnce(options);
    try {
      return await activeProjectSave;
    } finally {
      activeProjectSave = null;
    }
  },

  flushProjectSave: async (options = {}) => {
    let attempts = 0;
    while (get().currentProjectId && get().hasUnsavedChanges && attempts < 3) {
      attempts += 1;
      // eslint-disable-next-line no-await-in-loop
      await get().saveProject(options);
    }
    return !get().hasUnsavedChanges;
  },

  // Update existing project
  _saveProjectOnce: async ({ force = false, _retried = false } = {}) => {
    const state = get();
    const projectId = state.currentProjectId;
    
    // If no current project, save as new
    if (!projectId) {
      return state.saveProjectAsNew(state.projectName, state.projectDescription);
    }
    
    set({ isSavingProject: true, projectError: null });
    
    try {
      const updateData = projectSavePayload(state);
      const savedSignature = JSON.stringify(updateData);
      // E1 · SAVE_CONFLICT_GUARD: mandamos la versión sobre la que estamos
      // trabajando para que el servidor rechace el guardado si alguien
      // (otra pestaña u el agente por MCP) escribió algo más nuevo.
      if (SAVE_CONFLICT_GUARD && !force && state.currentProjectVersion != null) {
        updateData.base_version = state.currentProjectVersion;
      }
      // Sobrescritura consentida desde el modal de conflicto: el servidor la
      // distingue de una pestaña vieja (que guardaría sin versión ni force).
      if (force) updateData.force = true;
      
      const started = performance.now();
      recordAd2Metric('save.request');
      recordAd2Metric('save.payload_bytes', new Blob([savedSignature]).size);
      const result = await projectService.updateProject(projectId, updateData);
      recordAd2Metric('save.write');
      recordAd2Metric('save.latency_ms', performance.now() - started);
      
      if (result.success) {
        const changedDuringSave = projectSaveSignature(get()) !== savedSignature;
        set({
          currentProjectData: result.project,
          currentProjectVersion: result.project?.version ?? null,
          savedProjects: updateProjectSummary(get().savedProjects, result.project),
          isSavingProject: false,
          hasUnsavedChanges: changedDuringSave,
          saveConflict: null,
        });
        
        return result.project;
      }
      
      throw new Error('Failed to update project');
    } catch (error) {
      if (error.response?.status === 409) {
        const serverVersion = error.response.data?.detail?.server_version ?? null;
        set({ isSavingProject: false });
        // Envío LMX-T · antes del modal: casi siempre la versión nueva es de
        // LMX o del orquestador (un resultado). Se fusiona con el merge seguro
        // (no pisa lo del usuario) y se guarda otra vez, UNA sola vez. El
        // modal queda para un choque real (los dos cambiaron lo mismo).
        if (!force && !_retried && get().syncFromAgent) {
          const merged = await get().syncFromAgent();
          if (merged?.ok) return get()._saveProjectOnce({ _retried: true });
          if (merged?.conflict) return null;   // ya mostró el modal
        }
        await get().handleVersionConflict(serverVersion);
        return null;
      }
      set({ isSavingProject: false, projectError: error.message });
      console.error('Error updating project:', error);
      throw error;
    }
  },

  // ── E1 · SAVE_CONFLICT_GUARD ──────────────────────────
  // Sin cambios locales el proyecto se recarga en silencio; con cambios
  // locales se le pregunta al usuario (modal) para no perder su trabajo.
  saveConflict: null,

  handleVersionConflict: async (serverVersion) => {
    const state = get();
    if (!state.hasUnsavedChanges) {
      await state.loadProject(state.currentProjectId, { silent: true });
      return;
    }
    set({
      saveConflict: {
        projectName: state.projectName,
        serverVersion,
      },
    });
  },

  resolveConflictReload: async () => {
    const projectId = get().currentProjectId;
    set({ saveConflict: null, hasUnsavedChanges: false });
    await get().loadProject(projectId, { silent: true });
  },

  resolveConflictForce: async () => {
    set({ saveConflict: null });
    return get().saveProject({ force: true });
  },

  // E6c.1 · LMX: merge a 3 vías (base = última versión cargada/guardada,
  // local = canvas, remote = servidor). Sólo cae al modal si el usuario y el
  // agente cambiaron EL MISMO nodo de forma distinta.
  // onNew se llama en el mismo tick en que los nodos entran al store (para que
  // la animación los oculte antes de que se vean).
  syncFromAgent: async ({ onNew } = {}) => {
    const start = get();
    if (!start.currentProjectId || start.isSavingProject || start.saveConflict) return { ok: false };
    let remote;
    try {
      const result = await projectService.getProject(start.currentProjectId);
      remote = result?.project;
    } catch (_e) { return { ok: false }; }
    const s = get(); // estado fresco: pudo cambiar durante el fetch
    if (!remote || s.currentProjectId !== remote.id) return { ok: false };
    if (s.currentProjectVersion != null && remote.version != null
        && remote.version <= s.currentProjectVersion) return { ok: true, newNodeIds: [] };
    const base = s.currentProjectData || { nodes: [], edges: [] };
    const N = merge3(base.nodes || [], s.nodes, remote.nodes || [], nodeKey(withoutOutput));
    N.items = applyServerOutputs(N.items, base.nodes || [], remote.nodes || []);
    const E = merge3(base.edges || [], s.edges, remote.edges || [], edgeKey);
    if (N.conflict || E.conflict) {
      await s.handleVersionConflict(remote.version);
      return { ok: false, conflict: true };
    }
    const maxId = N.items.reduce((m, n) => {
      const x = n.id?.match(/^node_(\d+)$/); return x ? Math.max(m, +x[1]) : m;
    }, 0);
    const { nodes: stamped, changed } = stampExternal(N.items, s.nodes, sanitizeNodeData);
    markHydrating([...changed, ...N.added]);
    set({
      nodes: stamped,
      edges: E.items,
      nodeIdCounter: Math.max(s.nodeIdCounter || 1, remote.node_id_counter || 1, maxId + 1),
      currentProjectData: remote,
      currentProjectVersion: remote.version ?? s.currentProjectVersion,
      // hasUnsavedChanges NO se toca: si el usuario tenía cambios, siguen
      // pendientes; el próximo guardado ya parte de la versión del servidor.
    });
    try { onNew?.({ nodeIds: N.added, edgeIds: E.added }); } catch (_e) { /* sólo visual */ }
    return { ok: true, newNodeIds: N.added };
  },

  // Al volver a enfocar la pestaña: si el servidor tiene una versión más
  // nueva y no hay cambios locales, se recarga sin molestar al usuario.
  checkProjectVersion: async () => {
    const state = get();
    if (!SAVE_CONFLICT_GUARD || !state.currentProjectId) return;
    if (state.isSavingProject || state.saveConflict) return;
    try {
      const result = await projectService.getProject(state.currentProjectId);
      const serverVersion = result?.project?.version;
      if (serverVersion == null) return;
      if (state.currentProjectVersion != null && serverVersion > state.currentProjectVersion) {
        await state.handleVersionConflict(serverVersion);
      }
    } catch (_e) { /* sin conexión: se reintenta al próximo foco */ }
  },
  
  // Load projects from database
  loadSavedProjects: async (throwOnError = false) => {
    set({ isLoadingProjects: true, projectError: null });
    
    try {
      const result = await projectService.getProjects();
      
      set({
        savedProjects: result.projects || [],
        isLoadingProjects: false,
      });
      
      return result.projects || [];
    } catch (error) {
      const msg = error.message || 'Unknown error';
      set({ isLoadingProjects: false, projectError: msg });
      console.error('Error loading projects:', msg);
      if (throwOnError) throw error;
      return [];
    }
  },
  
  // Load a specific project
  loadProject: async (projectId, { silent = false } = {}) => {
    const before = get();
    if (before.currentProjectId && before.currentProjectId !== projectId
        && before.hasUnsavedChanges) {
      await before.flushProjectSave();
      if (get().hasUnsavedChanges) {
        set({ projectError: 'No se pudo guardar el proyecto actual. No se cambió de proyecto.' });
        return false;
      }
    }
    set({ isLoadingProjects: true, projectError: null });
    
    try {
      const result = await projectService.getProject(projectId);
      
      if (result.success && result.project) {
        const project = result.project;
        
        // Fix: backend returns node_id_counter (snake_case), frontend used nodeIdCounter (camelCase)
        // Also calculate the safe counter from existing node IDs to prevent collisions
        const savedCounter = project.node_id_counter || project.nodeIdCounter || 1;
        const maxNodeId = (project.nodes || []).reduce((max, node) => {
          const match = node.id?.match(/^node_(\d+)$/);
          return match ? Math.max(max, parseInt(match[1], 10)) : max;
        }, 0);
        const safeNodeIdCounter = Math.max(savedCounter, maxNodeId + 1);
        // Los nodos vienen de afuera: se marcan para que se remonten leyendo
        // el dato nuevo y su escritura de montaje no prenda "sin guardar".
        const { nodes: stamped } = stampExternal(project.nodes || [], get().nodes, sanitizeNodeData);
        markHydrating(stamped.map((n) => n.id));

        set({
          nodes: stamped,
          edges: project.edges || [],
          nodeIdCounter: safeNodeIdCounter,
          currentProjectId: project.id,
          currentProjectData: project,
          currentProjectVersion: project.version ?? null,
          projectName: project.name,
          projectDescription: project.description || '',
          selectedNode: null,
          isLoadingProjects: false,
          hasUnsavedChanges: false,
          saveConflict: null,
        });
        
        localStorage.setItem(projectPointerKey(get().userKey), project.id);
        if (silent) {
          // Recarga en silencio tras un conflicto: sin toasts ni modales.
        }
        return true;
      }
      
      throw new Error('Project not found');
    } catch (error) {
      set({ isLoadingProjects: false, projectError: error.message });
      console.error('Error loading project:', error);
      return false;
    }
  },
  
  // Delete a saved project
  deleteProject: async (projectId) => {
    try {
      await projectService.deleteProject(projectId);
      
      // Refresh projects list
      const projectsResult = await projectService.getProjects();
      
      set({ savedProjects: projectsResult.projects || [] });
      
      // If deleted project was current, reset
      if (get().currentProjectId === projectId) {
        set({ 
          currentProjectId: null, 
          currentProjectData: null,
          hasUnsavedChanges: false,
        });
        localStorage.removeItem(projectPointerKey(get().userKey));
      }
      
      return true;
    } catch (error) {
      console.error('Error deleting project:', error);
      set({ projectError: error.message });
      return false;
    }
  },
  
  // Duplicate a project
  duplicateProject: async (projectId, newName = null) => {
    try {
      const result = await projectService.duplicateProject(projectId, newName);
      
      if (result.success) {
        // Refresh projects list
        const projectsResult = await projectService.getProjects();
        set({ savedProjects: projectsResult.projects || [] });
        return result.project;
      }
      
      throw new Error('Failed to duplicate project');
    } catch (error) {
      console.error('Error duplicating project:', error);
      set({ projectError: error.message });
      throw error;
    }
  },
  
  // Create new project (clear canvas)
  newProject: () => {
    set({
      nodes: [],
      edges: [],
      nodeIdCounter: 1,
      currentProjectId: null,
      currentProjectData: null,
      projectName: 'Proyecto sin título',
      projectDescription: '',
      selectedNode: null,
      hasUnsavedChanges: false,
    });
    localStorage.removeItem(projectPointerKey(get().userKey));
  },
  
  // Try to load last opened project on startup, ensure default exists
  /** Identidad del usuario para aislar el puntero de "último proyecto". */
  userKey: null,
  setUserKey: (userKey) => set({ userKey: userKey || null }),

  initializeLastProject: async (userKey = null) => {
    if (userKey && get().userKey !== userKey) set({ userKey });
    const pointerKey = projectPointerKey(userKey || get().userKey);
    // Retry helper: waits for auth interceptor to be ready (handles 401 race condition)
    const callWithRetry = async (fn, retries = 4, delay = 600) => {
      for (let i = 0; i < retries; i++) {
        try {
          return await fn();
        } catch (e) {
          const is401 = e.response?.status === 401 || e.message?.includes('401') || e.message?.includes('Unauthorized');
          if (is401 && i < retries - 1) {
            console.debug(`[Projects] Auth not ready, retry ${i + 1}/${retries} in ${delay}ms...`);
            await new Promise(r => setTimeout(r, delay));
            continue;
          }
          throw e;
        }
      }
    };

    // 1. Ensure user has at least one project + migrate orphan executions
    let defaultProjectId = null;
    try {
      const res = await callWithRetry(() => axios.get(`${API}/projects/ensure-default`));
      defaultProjectId = res.data.project?.id || null;
      if (res.data.orphan_executions_migrated > 0) {
        console.debug(`[Projects] Migrated ${res.data.orphan_executions_migrated} orphan executions`);
      }
    } catch (e) {
      console.warn('[Projects] ensure-default failed:', e.message);
    }

    // 2. Load the list of projects (with retry for auth timing)
    try {
      await callWithRetry(async () => {
        await get().loadSavedProjects(true);
      });
    } catch (e) {
      console.warn('[Projects] loadSavedProjects failed after retries:', e.message);
    }

    // 3. Restaurar el último proyecto de ESTE usuario (si hay uno).
    const lastProjectId = localStorage.getItem(pointerKey);
    if (lastProjectId) {
      try {
        await callWithRetry(() => get().loadProject(lastProjectId));
        return;
      } catch {
        localStorage.removeItem(pointerKey);
      }
    }

    // 4. Sin puntero propio: arrancar en el proyecto por defecto (vacío) y no
    //    en "el primero de la lista", que solía traer un flujo viejo ajeno.
    const projects = get().savedProjects;
    const preferido = defaultProjectId
      || (projects.length === 1 ? projects[0].id : null);
    if (preferido) {
      try {
        await callWithRetry(() => get().loadProject(preferido));
      } catch (e) {
        console.warn('[Projects] Failed to load default project:', e.message);
      }
    }
  },
  
  // Export project as JSON file
  exportProject: () => {
    const project = {
      name: get().projectName,
      nodes: get().nodes,
      edges: get().edges,
      nodeIdCounter: get().nodeIdCounter,
      exportedAt: new Date().toISOString(),
      version: '1.0',
    };
    
    const blob = new Blob([JSON.stringify(project, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${get().projectName.replace(/\s+/g, '_')}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  },
  
  // Import project from JSON file
  importProject: (jsonData) => {
    try {
      const project = typeof jsonData === 'string' ? JSON.parse(jsonData) : jsonData;
      
      if (project.nodes && project.edges) {
        set({
          nodes: project.nodes,
          edges: project.edges,
          nodeIdCounter: project.nodeIdCounter || project.nodes.length + 1,
          projectName: project.name || 'Proyecto importado',
          currentProject: null,
          selectedNode: null,
        });
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error importing project:', error);
      return false;
    }
  },
}));
